import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { ServiceRole } from '../interfaces/service-role';

/**
 * Guard to protect the financial report route.
 *
 * Checks if the user has at least one role with allowAccessToFinancialReports permission.
 * Redirects to Part B list if the user doesn't have permission.
 */
@Injectable({
  providedIn: 'root'
})
export class FinancialReportGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    private readonly http: HttpClient,
    @Inject(LOCALE_ID) private readonly locale: string
  ) {}

  canActivate(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _route: ActivatedRouteSnapshot,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Fetch roles for 'all' services (service_id=null) to check permission
    const rolesUrl = `${environment.BASE_URL}${this.locale}/backend/services/all/roles`;

    return this.http.get<ServiceRole[]>(rolesUrl).pipe(
      map((roles: ServiceRole[]) => {
        // Check if any role has allowAccessToFinancialReports permission
        const hasPermission = roles.some(
          (role) => role.allowAccessToFinancialReports === true
        );

        if (hasPermission) {
          return true;
        }

        // Redirect to Part B if no permission
        console.warn(
          'Financial report access denied: No role with allowAccessToFinancialReports permission'
        );
        return this.router.parseUrl('/part-b');
      }),
      catchError((error) => {
        console.error('Error checking financial report permission:', error);
        // On error, redirect to Part B as a safe fallback
        return of(this.router.parseUrl('/part-b'));
      })
    );
  }
}
